/**
 * Analytics Store - server-side visitor tracking via edge function
 */

import { supabase } from "@/integrations/supabase/client";

export interface VisitRecord {
  id: string;
  timestamp: number;
  date: string;
  visitorId: string;
  deviceType: "Mobile" | "Desktop" | "Tablet";
  deviceModel?: string;
  userAgent: string;
  country?: string;
  city?: string;
  ip?: string;
  path: string;
}

export interface AnalyticsSummary {
  totalVisits: number;
  uniqueVisitors: number;
  todayVisits: number;
  weekVisits: number;
  monthVisits: number;
  deviceBreakdown: { mobile: number; desktop: number; tablet: number };
  dailyCounts: { date: string; visits: number; unique: number }[];
  locationBreakdown: { location: string; count: number }[];
  deviceModelBreakdown: { model: string; count: number }[];
}

const VISITOR_ID_KEY = "khays_visitor_id";

function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = `v_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
}

function detectDeviceType(): "Mobile" | "Desktop" | "Tablet" {
  const ua = navigator.userAgent.toLowerCase();
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "Tablet";
  if (/mobile|iphone|ipod|android.*mobile|windows phone/i.test(ua)) return "Mobile";
  return "Desktop";
}

function detectDeviceModel(): string {
  const ua = navigator.userAgent;
  // iOS devices
  const iosMatch = ua.match(/\(([^)]*iPhone[^)]*)\)/i) || ua.match(/\(([^)]*iPad[^)]*)\)/i) || ua.match(/\(([^)]*iPod[^)]*)\)/i);
  if (iosMatch) {
    const parts = iosMatch[1].split(";").map(s => s.trim());
    return parts[0] || "iOS Device";
  }
  // Android devices
  const androidMatch = ua.match(/;\s*([^;)]+)\s*Build\//i);
  if (androidMatch) return androidMatch[1].trim();
  // Windows
  if (/Windows NT/i.test(ua)) return "Windows PC";
  // Mac
  if (/Macintosh/i.test(ua)) return "Mac";
  // Linux
  if (/Linux/i.test(ua)) return "Linux PC";
  return "";
}

function getToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Load visits from cloud (app_data table) */
async function loadVisitsFromCloud(): Promise<VisitRecord[]> {
  try {
    const { data } = await supabase
      .from("app_data")
      .select("data_value")
      .eq("data_key", "khays_analytics")
      .maybeSingle();
    if (data?.data_value && Array.isArray(data.data_value)) {
      return data.data_value as unknown as VisitRecord[];
    }
  } catch {}
  return [];
}

/** Fallback: load from localStorage for backward compat */
function loadVisitsLocal(): VisitRecord[] {
  try {
    const stored = localStorage.getItem("khays_analytics");
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

/** Track visit via server-side edge function (gets real IP) */
export async function trackVisit(path: string = "/"): Promise<void> {
  const visitorId = getVisitorId();
  let tenantId: string | null = null;
  try {
    const { getCurrentTenantId } = await import("@/lib/tenantContext");
    tenantId = getCurrentTenantId();
  } catch { /* noop */ }
  try {
    await supabase.functions.invoke("track-visit", {
      body: {
        path,
        userAgent: navigator.userAgent,
        deviceType: detectDeviceType(),
        deviceModel: detectDeviceModel(),
        visitorId,
        tenantId,
        referrer: typeof document !== "undefined" ? document.referrer || null : null,
      },
    });
  } catch {
    // Silent fail — don't block page load
  }
}

/** Async summary that loads from cloud first, falls back to localStorage */
export async function getAnalyticsSummaryAsync(startDate?: string, endDate?: string): Promise<AnalyticsSummary> {
  let visits = await loadVisitsFromCloud();
  // Merge any old localStorage visits
  const localVisits = loadVisitsLocal();
  if (localVisits.length > 0 && visits.length === 0) {
    visits = localVisits;
  }
  return computeSummary(visits, startDate, endDate);
}

/** Sync version using localStorage only (backward compat) */
export function getAnalyticsSummary(startDate?: string, endDate?: string): AnalyticsSummary {
  const visits = loadVisitsLocal();
  return computeSummary(visits, startDate, endDate);
}

function computeSummary(allVisits: VisitRecord[], startDate?: string, endDate?: string): AnalyticsSummary {
  let visits = allVisits;
  if (startDate) visits = visits.filter((v) => v.date >= startDate);
  if (endDate) visits = visits.filter((v) => v.date <= endDate);

  const today = getToday();
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

  const uniqueIds = new Set(visits.map((v) => v.visitorId));
  const todayVisits = visits.filter((v) => v.date === today).length;
  const weekVisits = visits.filter((v) => v.date >= weekAgo).length;
  const monthVisits = visits.filter((v) => v.date >= monthAgo).length;
  const deviceBreakdown = {
    mobile: visits.filter((v) => v.deviceType === "Mobile").length,
    desktop: visits.filter((v) => v.deviceType === "Desktop").length,
    tablet: visits.filter((v) => v.deviceType === "Tablet").length,
  };

  const dailyMap = new Map<string, { visits: number; visitors: Set<string> }>();
  visits.forEach((v) => {
    if (!dailyMap.has(v.date)) dailyMap.set(v.date, { visits: 0, visitors: new Set() });
    const entry = dailyMap.get(v.date)!;
    entry.visits++; entry.visitors.add(v.visitorId);
  });
  const dailyCounts = Array.from(dailyMap.entries()).map(([date, data]) => ({ date, visits: data.visits, unique: data.visitors.size })).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);

  const locationMap = new Map<string, number>();
  visits.forEach((v) => {
    const loc = v.city && v.country ? `${v.city}, ${v.country}` : v.country || "Unknown";
    locationMap.set(loc, (locationMap.get(loc) || 0) + 1);
  });
  const locationBreakdown = Array.from(locationMap.entries()).map(([location, count]) => ({ location, count })).sort((a, b) => b.count - a.count).slice(0, 20);

  const modelMap = new Map<string, number>();
  visits.forEach((v) => {
    const model = (v as any).deviceModel || "Unknown";
    if (model && model !== "Unknown") modelMap.set(model, (modelMap.get(model) || 0) + 1);
  });
  const deviceModelBreakdown = Array.from(modelMap.entries()).map(([model, count]) => ({ model, count })).sort((a, b) => b.count - a.count).slice(0, 20);

  return { totalVisits: visits.length, uniqueVisitors: uniqueIds.size, todayVisits, weekVisits, monthVisits, deviceBreakdown, dailyCounts, locationBreakdown, deviceModelBreakdown };
}

/** Reset all analytics data */
export async function resetAnalytics(): Promise<void> {
  localStorage.removeItem("khays_analytics");
  try {
    await supabase.from("app_data").upsert(
      { data_key: "khays_analytics", data_value: [], updated_at: new Date().toISOString() },
      { onConflict: "data_key" }
    );
  } catch {}
}
